import React from 'react';
import { ScoredRace, RaceTag, RunnerTag } from '../../types/races';
import { format } from 'date-fns';

interface SystemRaceCardProps {
  race: ScoredRace;
  isExpanded: boolean;
  onToggle: () => void;
}

const formatRaceTime = (date: string | undefined) => {
  if (!date) return 'N/A';
  return format(new Date(date), 'HH:mm');
};

const getTagColor = (tag: RaceTag | RunnerTag) => {
  switch (tag) {
    case 'Very Strong Favourite':
      return 'bg-green-100 text-green-800';
    case 'Strong Favourite':
      return 'bg-yellow-100 text-yellow-800';
    case 'Favourite':
      return 'bg-orange-100 text-orange-800';
    case 'Does Not Qualify':
      return 'bg-red-100 text-red-800';
    case 'Qualifying Race':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const SystemRaceCard: React.FC<SystemRaceCardProps> = ({ race, isExpanded, onToggle }) => {
  const raceInfo = [
    race.description?.raceType,
    race.description?.distance,
    race.description?.going
  ].filter(Boolean).join(', ');

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div 
        className="flex flex-col sm:flex-row justify-between items-start cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex-1 w-full">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-between flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  {formatRaceTime(race.marketStartTime)} - {race.event.venue} - {race.marketName}
                </h4>
                <button className="hidden sm:block text-gray-400 hover:text-gray-600">
                  <svg 
                    className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              <button className="sm:hidden text-gray-400 hover:text-gray-600">
                <svg 
                  className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {race.runners?.length || 0} runners
              </span>
              {race.status && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  race.status === 'OPEN' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {race.status}
                </span>
              )}
              {race.tag && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagColor(race.tag)}`}>
                  {race.tag}
                </span>
              )}
            </div>
            {raceInfo && (
              <p className="text-sm text-gray-500">{raceInfo}</p>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4">
          {/* Desktop view */}
          <div className="hidden sm:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Horse</th>
                  <th className="px-2 py-2 text-center">OR</th>
                  <th className="px-2 py-2 text-center">Form</th>
                  <th className="px-2 py-2 text-center">Last Run</th>
                  <th className="px-2 py-2 text-right">Odds</th>
                  <th className="px-2 py-2 text-right">Tag</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {race.runners?.map(runner => (
                  <tr key={runner.selectionId} className="text-sm">
                    <td className="px-2 py-2 text-gray-500">
                      {runner.position_in_market}
                    </td>
                    <td className="px-2 py-2 font-medium text-gray-900">
                      {runner.runnerName}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-500">
                      {runner.official_rating || '-'}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-500">
                      {runner.form_figures || '-'}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-500">
                      {runner.days_since_last_run !== undefined ? `${runner.days_since_last_run}d` : '-'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {runner.odds?.best_back ? (
                        <span className="text-blue-600 font-medium">
                          {runner.odds.best_back.price.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {runner.tag && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagColor(runner.tag)}`}>
                          {runner.tag}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile view */}
          <div className="sm:hidden space-y-4">
            {race.runners?.map((runner) => (
              <div key={runner.selectionId} className="bg-white p-3 rounded-lg border border-gray-100">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">#{runner.position_in_market}</span>
                    <h4 className="text-sm font-medium text-gray-900">{runner.runnerName}</h4>
                  </div>
                  {runner.odds?.best_back && (
                    <span className="text-blue-600 font-medium text-sm">
                      {runner.odds.best_back.price.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">OR:</span>
                    <span className="ml-1 text-gray-900">{runner.official_rating || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Form:</span>
                    <span className="ml-1 text-gray-900">{runner.form_figures || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Run:</span>
                    <span className="ml-1 text-gray-900">
                      {runner.days_since_last_run !== undefined ? `${runner.days_since_last_run}d` : '-'}
                    </span>
                  </div>
                </div>
                {runner.tag && (
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagColor(runner.tag)}`}>
                      {runner.tag}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemRaceCard; 